import { useEffect, useState, useRef } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import { Modal } from '../../../../components/modal';
import { toast } from 'react-toastify';
import { GET_MY_LOCATIONS_V2 } from '../../../../services/locationServices/queries';
import {
  useCreateLocationMutation,
  useGetLocationQuery,
  useUpdateLocationMutation,
} from '../../../../services/locationServices/__generated__/queries.generated';
import PhoneInput from 'react-phone-number-input';
import { isValidPhoneNumber } from 'libphonenumber-js';
import 'react-phone-number-input/style.css';
import NumericInput from '../../../../components/NumericInput';
import { streetAddressForLocationSave } from '../../../../helpers/formatAddress';
import { useBrandLocale } from '../../../../hooks/useBrandLocale';
import { asCountry, CountryCode } from '../../../../utils/regions';
import {
  isValidPostalCode,
  normalizePostalCode,
  postalCodeErrorMessage,
  postalCodeLabel,
  regionLabel,
} from '../../../../utils/postalCode';

const Container = styled.div`
  ${tw`mx-auto p-6 flex flex-col items-center justify-center`}
`;

const LocationSection = styled.div`
  ${tw`mb-6`}
`;

const SectionTitle = styled.h2`
  ${tw`text-xl font-bold mb-2`}
`;

const Description = styled.p`
  ${tw`text-text-secondary mb-4`}
`;

const InputContainer = styled.div`
  ${tw`w-full max-w-md mx-auto mt-6 mb-4`}
`;

const Label = styled.label`
  ${tw`block text-text-secondary font-medium mb-2`}
`;

const StyledInput = styled.input`
  ${tw`w-full p-2 border border-border-default rounded-md`}
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  &[type='number'] {
    -moz-appearance: textfield;
  }
`;

const StyledTextArea = styled.textarea`
  ${tw`w-full p-2 border border-border-default rounded-md`}
`;

const AutocompleteInput = styled(StyledInput)`
  ${tw`w-full`}
`;

const StyledPhoneInput = styled(PhoneInput)`
  & .PhoneInputInput {
    ${tw`w-full p-2 border border-border-default rounded-md`}
    ${props => (props as any).$hasError && tw`border-red-500`}
  }

  & .PhoneInputCountryIcon {
    ${tw`border border-border-default rounded-md`}
  }

  & .PhoneInputCountrySelect {
    ${tw`mr-2 p-2 border border-border-default rounded-md`}
  }
`;

const ButtonContainer = styled.div`
  ${tw`flex justify-center w-full`}
`;

const NextButton = styled.button<{ isActivated: boolean }>`
  ${tw`w-full py-3 rounded-lg font-semibold transition-colors border border-solid`}
  ${({ isActivated }) =>
    isActivated
      ? tw`bg-black cursor-pointer border-black text-white shadow-xs hover:bg-black-hover`
      : tw`bg-bg-secondary cursor-not-allowed border-border-default text-text-tertiary`}
`;

interface AddOrEditLocationModalProps {
  isOpen: boolean;
  onClose: () => void;
  locationId?: number;
  /**
   * Services site (spec 019 D24): jobs are taxed where the customer's home or
   * car is, so the location has no tax rate to set.
   */
  hideTaxRate?: boolean;
}

export const AddOrEditLocationModal = ({
  isOpen,
  onClose,
  locationId,
  hideTaxRate = false,
}: AddOrEditLocationModalProps) => {
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zipCode, setZipCode] = useState('');
  // Spec 028: the location's country (the site's until Places says otherwise).
  const { country: brandCountry } = useBrandLocale();
  const [country, setCountry] = useState<CountryCode>(brandCountry);
  const brandCountryRef = useRef<CountryCode>(brandCountry);
  brandCountryRef.current = brandCountry;
  useEffect(() => {
    if (!locationId) setCountry(brandCountry);
  }, [brandCountry, locationId]);
  const [latitude, setLatitude] = useState<number>(0);
  const [longitude, setLongitude] = useState<number>(0);
  const [taxRate, setTaxRate] = useState<number>(0);
  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const [description, setDescription] = useState('');
  const autocompleteInputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<any>(null);
  const [isGoogleMapsLoaded, setIsGoogleMapsLoaded] = useState(false);
  const [phoneError, setPhoneError] = useState('');

  const { loading: locationLoading } = useGetLocationQuery({
    variables: {
      id: locationId || 0,
    },
    skip: !locationId,
    onCompleted: ({ location }) => {
      if (location) {
        setName(location.name);
        setAddress(location.address);
        setCity(location.city);
        setState(location.state);
        setZipCode(location.zipCode);
        setLatitude(location.latitude);
        setLongitude(location.longitude);
        setTaxRate(location.taxRate);
        // Parse old format (+1-xxx-xxx-xxxx) to E.164 format if needed
        const phone = location.phoneNumber || '';
        if (phone && phone.includes('-') && !phone.startsWith('+')) {
          // Old format: +1-xxx-xxx-xxxx -> +1xxxxxxxxxx
          const cleaned = phone.replace(/[^\d+]/g, '');
          setPhoneNumber(cleaned);
        } else {
          setPhoneNumber(phone);
        }
        setDescription(location.description || '');
      }
    },
  });

  // Load Google Maps API script with new PlaceAutocompleteElement
  useEffect(() => {
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    if (!apiKey) {
      return;
    }

    // Validate that it's not an OAuth Client ID
    if (apiKey.includes('apps.googleusercontent.com')) {
      toast.error('Invalid API Key: Please use a Google Maps API Key, not an OAuth Client ID');
      return;
    }

    // Helper function to check if Google Maps API is fully loaded
    const checkGoogleMapsLoaded = (): boolean => {
      return !!(
        window.google &&
        window.google.maps &&
        window.google.maps.places &&
        window.google.maps.places.Autocomplete
      );
    };

    // Check if already fully loaded
    if (checkGoogleMapsLoaded()) {
      setIsGoogleMapsLoaded(true);
      return;
    }

    // Check if script tag already exists
    const existingScript = document.querySelector(`script[src*="maps.googleapis.com/maps/api/js"]`);

    if (existingScript) {
      // Script exists - wait for it to load and Places library to initialize
      let retryCount = 0;
      const MAX_RETRIES = 50; // Maximum 5 seconds of retries (50 * 100ms)

      const checkLoaded = () => {
        if (checkGoogleMapsLoaded()) {
          setIsGoogleMapsLoaded(true);
        } else if (retryCount < MAX_RETRIES) {
          retryCount++;
          // Retry after a short delay if Places library isn't ready yet
          setTimeout(checkLoaded, 100);
        } else {
        }
      };

      // If script is already loaded, check immediately
      if (existingScript.getAttribute('data-loaded') === 'true') {
        checkLoaded();
      } else {
        // Wait for script to load first
        const onScriptLoad = () => {
          existingScript.setAttribute('data-loaded', 'true');
          // After script loads, wait for Places library to initialize
          checkLoaded();
        };

        existingScript.addEventListener('load', onScriptLoad);

        // Also check if script is already loaded (load event might have fired)
        if (existingScript.getAttribute('src')) {
          // Check immediately in case script is already loaded
          setTimeout(() => {
            if (checkGoogleMapsLoaded()) {
              setIsGoogleMapsLoaded(true);
            } else {
              checkLoaded();
            }
          }, 0);
        }
      }
      return;
    }

    // Load Google Maps API script
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&loading=async`;
    script.async = true;
    script.defer = true;

    script.onload = () => {
      script.setAttribute('data-loaded', 'true');
      // Wait for Places library to initialize after script loads
      let retryCount = 0;
      const MAX_RETRIES = 50; // Maximum 5 seconds of retries (50 * 100ms)

      const checkLoaded = () => {
        if (checkGoogleMapsLoaded()) {
          setIsGoogleMapsLoaded(true);
        } else if (retryCount < MAX_RETRIES) {
          retryCount++;
          // Retry after a short delay
          setTimeout(checkLoaded, 100);
        } else {
        }
      };
      checkLoaded();
    };

    script.onerror = () => {
      toast.error('Failed to load Google Maps. Please check your API key.');
    };

    document.head.appendChild(script);

    return () => {
      // Cleanup: don't remove script as it might be used elsewhere
    };
  }, []);

  // Helper function to handle place selection
  const handlePlaceSelection = (place: any) => {
    if (!place.geometry || !place.geometry.location) {
      toast.error('No details available for the selected place');
      return;
    }

    // Extract address components
    let streetNumber = '';
    let route = '';
    let cityName = '';
    let sublocalityName = '';
    let postalTownName = '';
    let adminLevel2Name = '';
    let stateName = '';
    let zipCodeValue = '';
    let country = '';

    place.address_components?.forEach((component: any) => {
      const types = component.types;
      if (types.includes('street_number')) {
        streetNumber = component.long_name;
      }
      if (types.includes('route')) {
        route = component.long_name;
      }
      if (types.includes('locality')) {
        cityName = component.long_name;
      }
      if (types.includes('postal_town')) {
        postalTownName = component.long_name;
      }
      if (types.includes('sublocality_level_1') || types.includes('sublocality')) {
        sublocalityName = component.long_name;
      }
      if (types.includes('administrative_area_level_2')) {
        adminLevel2Name = component.long_name;
      }
      if (types.includes('administrative_area_level_1')) {
        stateName = component.short_name;
      }
      if (types.includes('postal_code')) {
        zipCodeValue = component.long_name;
      }
      if (types.includes('country')) {
        country = component.short_name;
      }
    });

    // Set address fields
    const fullAddress = place.formatted_address || '';
    setAddress(fullAddress);
    let resolvedCity = cityName || sublocalityName || postalTownName || adminLevel2Name;
    if (fullAddress) {
      const addressParts = fullAddress.split(',').map((part: string) => part.trim());
      const addressCity = addressParts.length >= 2 ? addressParts[1] : '';
      if (addressCity && addressCity !== stateName && addressCity !== country) {
        resolvedCity = addressCity;
      }
    }
    setCity(resolvedCity);
    setState(stateName);
    const placeCountry = country ? asCountry(country) : brandCountryRef.current;
    setCountry(placeCountry);
    setZipCode(normalizePostalCode(placeCountry, zipCodeValue) ?? zipCodeValue);

    // Handle location - could be lat()/lng() methods or direct lat/lng properties
    const location = place.geometry.location;
    const lat = typeof location.lat === 'function' ? location.lat() : location.lat;
    const lng = typeof location.lng === 'function' ? location.lng() : location.lng;
    setLatitude(lat);
    setLongitude(lng);

    // If name is empty, use the place name
    if (!name) {
      setName(place.name || '');
    }
  };

  // Initialize Google Places Autocomplete
  // Note: Using legacy Autocomplete API (deprecated but still functional)
  // Migration to PlaceAutocompleteElement can be done later if needed
  useEffect(() => {
    if (!isGoogleMapsLoaded || !autocompleteInputRef.current || !isOpen) {
      return;
    }

    // Clean up existing autocomplete if any
    if (autocompleteRef.current) {
      if (window.google?.maps?.event) {
        window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
      }
      autocompleteRef.current = null;
    }

    // Helper function to initialize autocomplete with retry logic
    const initializeAutocomplete = (retryCount = 0) => {
      // Double-check that Google Maps Places API is fully available
      if (!window.google?.maps?.places?.Autocomplete) {
        if (retryCount < 10) {
          // Retry after a short delay (up to 10 times = 1 second total)
          setTimeout(() => initializeAutocomplete(retryCount + 1), 100);
          return;
        } else {
          return;
        }
      }

      if (!autocompleteInputRef.current) {
        return;
      }

      try {
        // Using legacy Autocomplete (deprecated but still works)
        // This will show a warning but functionality remains
        // No types restriction to allow airports, establishments, addresses, etc.
        const autocomplete = new window.google.maps.places.Autocomplete(
          autocompleteInputRef.current,
          {
            fields: ['formatted_address', 'address_components', 'geometry', 'name'],
            // Spec 028: a Canadian site searches Canadian addresses.
            ...(brandCountryRef.current === 'CA'
              ? { componentRestrictions: { country: ['ca'] } }
              : {}),
          },
        );

        autocomplete.addListener('place_changed', () => {
          const place = autocomplete.getPlace();
          handlePlaceSelection(place);
        });

        autocompleteRef.current = autocomplete;
      } catch (error) {
        // Only show error if we've exhausted retries
        if (retryCount >= 10) {
          toast.error('Failed to initialize address autocomplete. Please check your API key.');
        } else {
          // Retry after a short delay
          setTimeout(() => initializeAutocomplete(retryCount + 1), 100);
        }
      }
    };

    // Small delay to ensure DOM is ready and input is fully rendered
    const timeoutId = setTimeout(() => {
      initializeAutocomplete();
    }, 50);

    return () => {
      clearTimeout(timeoutId);
      if (autocompleteRef.current && window.google?.maps?.event) {
        window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
        autocompleteRef.current = null;
      }
    };
  }, [isGoogleMapsLoaded, isOpen]);

  useEffect(() => {
    if (!locationId) {
      setName('');
      setAddress('');
      setCity('');
      setState('');
      setZipCode('');
      setLatitude(0);
      setLongitude(0);
      setTaxRate(0);
      setPhoneNumber('');
      setDescription('');
    }
  }, [locationId]);

  useEffect(() => {
    const e2ePlace = (window as any).__e2ePlace;
    if (isOpen && e2ePlace) {
      setIsGoogleMapsLoaded(true);
      handlePlaceSelection(e2ePlace);
    }
  }, [isOpen]);

  const [createLocation] = useCreateLocationMutation({
    refetchQueries: [{ query: GET_MY_LOCATIONS_V2, variables: { input: { page: 1, limit: 20 } } }],
    onCompleted: () => {
      toast.success('Location created successfully');
      onClose();
    },
    onError: error => {
      const graphQLErrorMessage = error?.graphQLErrors?.[0]?.message;
      const errorMessage = graphQLErrorMessage?.replace(/^GraphQL error:\s*/i, '');
      toast.error(errorMessage || 'Failed to create location');
    },
  });

  const [updateLocation] = useUpdateLocationMutation({
    refetchQueries: [{ query: GET_MY_LOCATIONS_V2, variables: { input: { page: 1, limit: 20 } } }],
    onCompleted: () => {
      toast.success('Location updated successfully');
      onClose();
    },
    onError: () => {
      toast.error('Failed to update location');
    },
  });

  const onSubmitLocation = () => {
    // 1. Check for blank fields
    if (!name.trim()) {
      toast.error('Location name is required');
      return;
    }
    if (!address.trim()) {
      toast.error('Address is required');
      return;
    }
    if (!city.trim()) {
      toast.error('City is required');
      return;
    }
    if (!state.trim()) {
      toast.error(`${regionLabel(country)} is required`);
      return;
    }
    if (!zipCode.trim()) {
      toast.error(`${postalCodeLabel(country)} is required`);
      return;
    }
    // Spec 028: a Canadian postal code is checked here and on the server.
    if (country === 'CA' && !isValidPostalCode('CA', zipCode)) {
      toast.error(postalCodeErrorMessage('CA'));
      return;
    }
    if (!phoneNumber || !phoneNumber.trim()) {
      toast.error('Phone number is required');
      setPhoneError('Phone number is required');
      return;
    }

    // 2. Validate phone format using libphonenumber-js
    try {
      if (!isValidPhoneNumber(phoneNumber)) {
        toast.error('Invalid phone number format');
        setPhoneError('Please enter a valid phone number');
        return;
      }
    } catch (error) {
      toast.error('Invalid phone number format');
      setPhoneError('Please enter a valid phone number');
      return;
    }

    // Clear previous errors
    setPhoneError('');

    if (locationId) {
      const persistedAddress = streetAddressForLocationSave(address, city, state, zipCode);
      updateLocation({
        variables: {
          id: locationId,
          input: {
            name,
            address: persistedAddress,
            city,
            state,
            zipCode: country === 'CA' ? (normalizePostalCode('CA', zipCode) ?? zipCode) : zipCode,
            country,
            latitude,
            longitude,
            taxRate,
            phoneNumber,
            description,
          },
        },
      });
    } else {
      const persistedAddress = streetAddressForLocationSave(address, city, state, zipCode);
      createLocation({
        variables: {
          createLocationInput: {
            name,
            address: persistedAddress,
            city,
            state,
            zipCode: country === 'CA' ? (normalizePostalCode('CA', zipCode) ?? zipCode) : zipCode,
            country,
            latitude,
            longitude,
            taxRate,
            phoneNumber,
            description,
          },
        },
      });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      {locationLoading ? (
        <div>Loading...</div>
      ) : (
        <Container>
          <LocationSection>
            <SectionTitle>Location</SectionTitle>
            <Description>Needs your location to ensure we operate where you are</Description>

            <InputContainer>
              <Label>Name</Label>
              <StyledInput
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="Location name"
                data-testid="location-name-input"
              />
            </InputContainer>

            <InputContainer>
              <Label>Address (Start typing to search)</Label>
              {/* Hidden fake input to trick browser autocomplete */}
              <input
                type="text"
                autoComplete="chrome-off"
                style={{ position: 'absolute', opacity: 0, height: 0, width: 0 }}
                tabIndex={-1}
                readOnly
              />
              <AutocompleteInput
                ref={autocompleteInputRef}
                type="text"
                value={address}
                onChange={e => setAddress(e.target.value)}
                placeholder="Search for an address..."
                disabled={!isGoogleMapsLoaded}
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck="false"
                data-testid="location-address-input"
              />
              {!isGoogleMapsLoaded && (
                <p style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                  Loading Google Maps...
                </p>
              )}
            </InputContainer>

            {/* Read-only fields populated from autocomplete */}
            <InputContainer>
              <Label>City</Label>
              <StyledInput
                type="text"
                value={city}
                readOnly
                placeholder="Auto-filled from address"
                style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                data-testid="location-city-input"
              />
            </InputContainer>

            <InputContainer>
              <Label>{regionLabel(country)}</Label>
              <StyledInput
                type="text"
                value={state}
                readOnly
                placeholder="Auto-filled from address"
                style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                data-testid="location-state-input"
              />
            </InputContainer>

            <InputContainer>
              <Label>{country === 'CA' ? 'Postal Code' : 'Zip Code'}</Label>
              <StyledInput
                type="text"
                value={zipCode}
                onChange={e => setZipCode(e.target.value)}
                placeholder="Auto-filled from address or enter manually"
                style={zipCode ? { backgroundColor: '#f5f5f5' } : {}}
                data-testid="location-zip-input"
              />
              {!zipCode && (
                <p style={{ fontSize: '12px', color: '#e53e3e', marginTop: '4px' }}>
                  {postalCodeLabel(country)} is required. Please enter it manually if not
                  auto-filled.
                </p>
              )}
            </InputContainer>

            {/* Hidden latitude/longitude fields (still needed for backend) */}
            <input type="hidden" value={latitude} />
            <input type="hidden" value={longitude} />

            {!hideTaxRate && (
              <InputContainer>
                <Label>Tax Rate (%)</Label>
                <NumericInput
                  allowDecimal={true}
                  value={taxRate}
                  onValueChange={(value: number | null) => setTaxRate(value ?? 0)}
                  placeholder="Tax rate"
                  width="100%"
                  data-testid="location-tax-rate-input"
                />
              </InputContainer>
            )}

            <InputContainer>
              <Label>Phone Number</Label>
              <StyledPhoneInput
                international
                defaultCountry={brandCountry}
                value={phoneNumber}
                onChange={(value: string | undefined) => {
                  setPhoneNumber(value || '');
                  if (phoneError) setPhoneError('');
                }}
                onBlur={() => {
                  // Validate on blur
                  if (phoneNumber) {
                    if (!isValidPhoneNumber(phoneNumber)) {
                      setPhoneError('Invalid phone number format');
                    } else {
                      setPhoneError('');
                    }
                  }
                }}
                placeholder="Enter phone number"
                $hasError={!!phoneError}
                data-testid="location-phone-input"
              />
              {phoneError && (
                <p style={{ fontSize: '12px', color: '#e53e3e', marginTop: '4px' }}>{phoneError}</p>
              )}
            </InputContainer>

            <InputContainer>
              <Label>Description</Label>
              <StyledTextArea
                value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder="Location description"
                rows={4}
                data-testid="location-description-input"
              />
            </InputContainer>
          </LocationSection>

          <ButtonContainer>
            <NextButton
              isActivated={true}
              onClick={onSubmitLocation}
              data-testid="location-save-button"
            >
              Save Location
            </NextButton>
          </ButtonContainer>
        </Container>
      )}
    </Modal>
  );
};
